// apps/admin-ui/src/config/contractRules.ts
// Contract rules manifest — "source of truth for humans"
// Generated from @cxxr/contracts source
// ALL RIGHTS RESERVED

// =============================================================================
// TYPES
// =============================================================================

export interface ContractConstant {
  name: string;
  value: string | number | boolean;
  unit?: string;
  section?: string; // Whitepaper reference
}

export interface DecisionRow {
  condition: string;
  outcome: string;
  type: 'allow' | 'deny' | 'fee' | 'info';
}

export interface ContractRule {
  id: string;
  summary: string;
  details?: string;
  section?: string;
  decisions?: DecisionRow[];
}

export interface ContractDefinition {
  id: string;
  name: string;
  domain: string;
  domainColor: string;
  description: string;
  sections: string[];          // Whitepaper references
  constants: ContractConstant[];
  rules: ContractRule[];
  services: string[];          // Which services import this
  sourceFile: string;
}

// =============================================================================
// DOMAIN COLORS
// =============================================================================

const DOMAIN_COLORS: Record<string, string> = {
  fees: 'var(--color-warning)',
  token: 'var(--color-brand-500)',
  reserve: 'var(--color-info)',
  exchange: 'var(--color-success)',
  compliance: 'var(--color-error)',
  governance: '#8b5cf6',
};

// =============================================================================
// CONTRACT DEFINITIONS
// =============================================================================

export const CONTRACT_RULES: ContractDefinition[] = [
  // ─────────────────────────────────────────────────────────────────
  // FEES DOMAIN
  // ─────────────────────────────────────────────────────────────────
  {
    id: 'FeeContract',
    name: 'Fee Contract',
    domain: 'fees',
    domainColor: DOMAIN_COLORS.fees,
    description: 'Business rules for fee calculation and three-way distribution.',
    sections: ['§2.3.1 Fee Allocation', '§1.1.2 Token Operations'],
    constants: [
      { name: 'MINT_FEE_PERCENT', value: 5, unit: '%', section: '§1.1.2' },
      { name: 'EXCHANGE_FEE_PERCENT', value: 0.5, unit: '%', section: '§1.1.2' },
      { name: 'TRANSFER_FEE_PERCENT', value: 0.5, unit: '%', section: '§1.1.2' },
      { name: 'WITHDRAWAL_FEE_PERCENT', value: 0.5, unit: '%', section: '§1.1.2' },
      { name: 'SPLIT_CXXR_PERCENT', value: 47.5, unit: '%', section: '§2.3.1' },
      { name: 'SPLIT_CCC_PERCENT', value: 47.5, unit: '%', section: '§2.3.1' },
      { name: 'SPLIT_RESERVE_PERCENT', value: 5, unit: '%', section: '§2.3.1' },
    ],
    rules: [
      {
        id: 'fee-schedule',
        summary: 'Every transaction incurs a fee based on its type.',
        section: '§1.1.2',
        decisions: [
          { condition: 'Type = mint', outcome: '5% fee on USD deposited', type: 'fee' },
          { condition: 'Type = exchange (buy/sell)', outcome: '0.5% fee on amount', type: 'fee' },
          { condition: 'Type = transfer (P2P)', outcome: '0.5% fee on amount', type: 'fee' },
          { condition: 'Type = withdrawal (burn)', outcome: '0.5% fee on CXXR redeemed', type: 'fee' },
        ],
      },
      {
        id: 'fee-distribution',
        summary: 'Every fee collected is split three ways: CXXR operational (47.5%), CCC maintenance (47.5%), and Reserve dividend pool (5%).',
        section: '§2.3.1',
        decisions: [
          { condition: 'Fee collected', outcome: '47.5% → CXXR Treasury (operational/administrative)', type: 'info' },
          { condition: 'Fee collected', outcome: '47.5% → CCC (maintenance/infrastructure)', type: 'info' },
          { condition: 'Fee collected', outcome: '5% → Reserve dividend pool (RCXXR holder rewards)', type: 'info' },
        ],
      },
      {
        id: 'fee-validation',
        summary: 'Fee calculation rejects invalid inputs: unknown fee type, non-numeric amounts, zero or negative amounts.',
        decisions: [
          { condition: 'Fee type not in {mint, exchange, transfer, withdrawal}', outcome: 'Reject: invalid fee type', type: 'deny' },
          { condition: 'Amount ≤ 0 or non-numeric', outcome: 'Reject: invalid amount', type: 'deny' },
          { condition: 'Amount > MAX_SAFE_INTEGER', outcome: 'Reject: amount too large', type: 'deny' },
        ],
      },
    ],
    services: ['mint', 'redeem', 'exchange', 'ledger', 'transfer (indirect via TPE)'],
    sourceFile: 'contracts/fees/FeeContract.ts',
  },

  {
    id: 'DividendContract',
    name: 'Dividend Contract',
    domain: 'fees',
    domainColor: DOMAIN_COLORS.fees,
    description: 'Business rules for reserve token (RCXXR) dividend distribution from the fee pool.',
    sections: ['§1.1.2 Reward/Dividend Program'],
    constants: [
      { name: 'DIVIDEND_POOL_PERCENT', value: 5, unit: '% of all fees', section: '§2.3.1' },
      { name: 'MIN_RCXXR_FOR_CLAIM', value: 1, unit: 'RCXXR', section: '§1.1.2' },
      { name: 'MIN_DIVIDEND_AMOUNT', value: 0.01, unit: 'USD', section: '§1.1.2' },
      { name: 'CLAIM_WINDOW_DAYS', value: 90, unit: 'days', section: '§1.1.2' },
    ],
    rules: [
      {
        id: 'dividend-eligibility',
        summary: '5% of all transaction fees flow to the dividend pool. Only RCXXR holders may claim, pro-rata based on their RCXXR balance at the time of distribution.',
        section: '§1.1.2',
        decisions: [
          { condition: 'User holds ≥ 1 RCXXR', outcome: 'Eligible for pro-rata dividend claim', type: 'allow' },
          { condition: 'User holds < 1 RCXXR', outcome: 'Not eligible', type: 'deny' },
          { condition: 'Calculated share < $0.01', outcome: 'Below minimum; not distributed', type: 'deny' },
          { condition: 'Claim window > 90 days after distribution', outcome: 'Expired; funds return to pool', type: 'deny' },
        ],
      },
    ],
    services: ['ledger', 'tpe', 'wallet'],
    sourceFile: 'contracts/fees/DividendContract.ts',
  },

  // ─────────────────────────────────────────────────────────────────
  // TOKEN DOMAIN
  // ─────────────────────────────────────────────────────────────────
  {
    id: 'MintContract',
    name: 'Mint Contract',
    domain: 'token',
    domainColor: DOMAIN_COLORS.token,
    description: 'Business rules for CXXR token minting (issuance). Minting creates new CXXR tokens in exchange for USD deposits.',
    sections: ['§1.1.2 Token Issuance'],
    constants: [
      { name: 'MIN_MINT_AMOUNT', value: 10, unit: 'USD', section: '§1.1.2' },
      { name: 'MAX_MINT_AMOUNT', value: 100_000, unit: 'USD', section: '§1.1.2' },
      { name: 'MAX_DAILY_MINT_AMOUNT', value: 500_000, unit: 'USD', section: '§1.1.2' },
      { name: 'MAX_MONTHLY_MINT_AMOUNT', value: 2_000_000, unit: 'USD', section: '§1.1.2' },
      { name: 'MINT_REQUIRED_KYC_LEVEL', value: 'verified', section: '§1.1.2' },
    ],
    rules: [
      {
        id: 'mint-validation',
        summary: 'A mint (deposit) is allowed only if the user is KYC-verified and the amount falls within per-transaction, daily, and monthly limits.',
        section: '§1.1.2',
        decisions: [
          { condition: 'KYC status ≠ verified', outcome: 'Deny: KYC verification required', type: 'deny' },
          { condition: 'Amount < $10', outcome: 'Deny: below minimum', type: 'deny' },
          { condition: 'Amount > $100,000', outcome: 'Deny: above per-transaction max', type: 'deny' },
          { condition: 'Daily total would exceed $500,000', outcome: 'Deny: daily limit exceeded', type: 'deny' },
          { condition: 'Monthly total would exceed $2,000,000', outcome: 'Deny: monthly limit exceeded', type: 'deny' },
          { condition: 'All checks pass', outcome: 'Allow: 5% fee applied, CXXR minted at 1:1 (net of fee)', type: 'allow' },
        ],
      },
    ],
    services: ['mint'],
    sourceFile: 'contracts/token/MintContract.ts',
  },

  {
    id: 'BurnContract',
    name: 'Burn Contract',
    domain: 'token',
    domainColor: DOMAIN_COLORS.token,
    description: 'Business rules for CXXR token burning (redemption/withdrawal). Burning removes CXXR from circulation in exchange for USD.',
    sections: ['§1.1.2 Token Operations'],
    constants: [
      { name: 'MIN_BURN_AMOUNT', value: 10, unit: 'CXXR', section: '§1.1.2' },
      { name: 'MAX_BURN_AMOUNT', value: 50_000, unit: 'CXXR', section: '§1.1.2' },
      { name: 'MAX_DAILY_BURN_AMOUNT', value: 100_000, unit: 'CXXR', section: '§1.1.2' },
      { name: 'MAX_MONTHLY_BURN_AMOUNT', value: 500_000, unit: 'CXXR', section: '§1.1.2' },
      { name: 'BURN_REQUIRED_KYC_LEVEL', value: 'verified', section: '§1.1.2' },
      { name: 'MIN_BANK_ACCOUNT_AGE_DAYS', value: 3, unit: 'days', section: '§1.1.2' },
    ],
    rules: [
      {
        id: 'burn-validation',
        summary: 'A withdrawal (burn) requires KYC verification, a bank account linked for at least 3 days, and the amount must be within per-transaction, daily, and monthly limits.',
        decisions: [
          { condition: 'KYC status ≠ verified', outcome: 'Deny: KYC required', type: 'deny' },
          { condition: 'Amount < 10 CXXR', outcome: 'Deny: below minimum', type: 'deny' },
          { condition: 'Amount > 50,000 CXXR', outcome: 'Deny: above per-transaction max', type: 'deny' },
          { condition: 'Bank account linked < 3 days', outcome: 'Deny: bank too new', type: 'deny' },
          { condition: 'Daily total > 100,000 CXXR', outcome: 'Deny: daily limit exceeded', type: 'deny' },
          { condition: 'Monthly total > 500,000 CXXR', outcome: 'Deny: monthly limit exceeded', type: 'deny' },
          { condition: 'All checks pass', outcome: 'Allow: 0.5% fee applied, USD sent via ACH', type: 'allow' },
        ],
      },
    ],
    services: ['redeem'],
    sourceFile: 'contracts/token/BurnContract.ts',
  },

  {
    id: 'TransferContract',
    name: 'Transfer Contract',
    domain: 'token',
    domainColor: DOMAIN_COLORS.token,
    description: 'Business rules for peer-to-peer CXXR transfers.',
    sections: ['§1.1.2 Transaction Fees'],
    constants: [
      { name: 'MIN_TRANSFER_AMOUNT', value: 1, unit: 'CXXR', section: '§1.1.2' },
      { name: 'MAX_TRANSFER_AMOUNT', value: 50_000, unit: 'CXXR', section: '§1.1.2' },
      { name: 'MAX_DAILY_TRANSFER_AMOUNT', value: 100_000, unit: 'CXXR', section: '§1.1.2' },
      { name: 'TRANSFER_REQUIRED_KYC_LEVEL', value: 'verified', section: '§1.1.2' },
    ],
    rules: [
      {
        id: 'transfer-validation',
        summary: 'Both sender and recipient must be KYC-verified. Amount must be within per-transaction and daily limits. Cannot send to self.',
        section: '§1.1.2',
        decisions: [
          { condition: 'Sender KYC ≠ verified', outcome: 'Deny: sender KYC required', type: 'deny' },
          { condition: 'Recipient KYC ≠ verified', outcome: 'Deny: recipient KYC required', type: 'deny' },
          { condition: 'Sender = recipient', outcome: 'Deny: cannot transfer to self', type: 'deny' },
          { condition: 'Amount < 1 CXXR', outcome: 'Deny: below minimum', type: 'deny' },
          { condition: 'Amount > 50,000 CXXR', outcome: 'Deny: above max', type: 'deny' },
          { condition: 'Daily total > 100,000 CXXR', outcome: 'Deny: daily limit', type: 'deny' },
          { condition: 'All checks pass', outcome: 'Allow: 0.5% fee applied', type: 'allow' },
        ],
      },
    ],
    services: ['transfer'],
    sourceFile: 'contracts/token/TransferContract.ts',
  },

  {
    id: 'SplitContract',
    name: 'Token Split Contract',
    domain: 'token',
    domainColor: DOMAIN_COLORS.token,
    description: 'Automatic token split mechanism triggered when market price deviates above the peg. Each CXXR held produces 1 tradable CXXR + 1 non-tradable RCXXR.',
    sections: ['§1.1.2 Token Split Mechanism'],
    constants: [
      { name: 'SPLIT_RATIO', value: 1, unit: ':1 (CXXR → CXXR + RCXXR)', section: '§1.1.2' },
      { name: 'DEFAULT_SPLIT_THRESHOLD_PRICE', value: 1.05, unit: 'USD', section: '§1.1.2' },
      { name: 'SPLIT_TRIGGER_PERCENT', value: 5.0, unit: '% above peg', section: '§1.1.2' },
      { name: 'SPLIT_TRIGGER_DURATION_HOURS', value: 24, unit: 'hours', section: '§1.1.2' },
      { name: 'MIN_SPLIT_INTERVAL_DAYS', value: 30, unit: 'days', section: '§1.1.2' },
      { name: 'MIN_DISPLAY_BALANCE', value: 0.01, unit: 'CXXR', section: '§1.1.2' },
    ],
    rules: [
      {
        id: 'split-trigger',
        summary: 'When market price exceeds the peg by ≥ 5% for ≥ 24 hours continuously, and at least 30 days have passed since the last split, the system triggers an automatic token split.',
        section: '§1.1.2',
        decisions: [
          { condition: 'Market price < $1.05', outcome: 'No split: price within threshold', type: 'info' },
          { condition: 'Price ≥ $1.05 but < 24 hours sustained', outcome: 'No split: duration not met', type: 'info' },
          { condition: 'Last split was < 30 days ago', outcome: 'No split: cooldown active', type: 'deny' },
          { condition: 'Price ≥ $1.05 sustained ≥ 24h, cooldown passed', outcome: 'Split: each holder gets 1 RCXXR per CXXR held', type: 'allow' },
        ],
      },
      {
        id: 'split-effect',
        summary: 'After a split, each user\'s CXXR balance is unchanged. They receive an equal amount of RCXXR (non-transferable reserve tokens). Only balances ≥ 0.01 CXXR participate.',
        decisions: [
          { condition: 'Balance < 0.01 CXXR', outcome: 'Excluded: dust balance', type: 'deny' },
          { condition: 'Balance ≥ 0.01 CXXR', outcome: 'Receives balance × 1 RCXXR', type: 'allow' },
        ],
      },
    ],
    services: ['tpe'],
    sourceFile: 'contracts/token/SplitContract.ts',
  },

  {
    id: 'VestingContract',
    name: 'Vesting Contract',
    domain: 'token',
    domainColor: DOMAIN_COLORS.token,
    description: 'Time-based RCXXR vesting for continuous CXXR holders. Quarterly minting replaces the price-based split as the primary RCXXR generation mechanism.',
    sections: ['§1.1.2 (revised interpretation)'],
    constants: [
      { name: 'VESTING_PERIOD_DAYS', value: 90, unit: 'days (quarterly)', section: '§1.1.2' },
      { name: 'VESTING_RATE', value: 1.0, unit: ':1 (CXXR balance → RCXXR)', section: '§1.1.2' },
      { name: 'MIN_BALANCE_FOR_VESTING', value: 1.0, unit: 'CXXR', section: '§1.1.2' },
      { name: 'MIN_HOLDING_DAYS', value: 7, unit: 'days', section: '§1.1.2' },
      { name: 'QUARTER_DAYS', value: 90, unit: 'days', section: '§1.1.2' },
    ],
    rules: [
      {
        id: 'vesting-eligibility',
        summary: 'At the end of each quarter, users who continuously held ≥ 1 CXXR receive RCXXR at a 1:1 rate. "Continuous" means balance never dropped to zero during the quarter. Pro-rata vesting for partial quarters (min 7 days).',
        decisions: [
          { condition: 'Balance dropped to 0 during quarter', outcome: 'Not eligible: continuity broken', type: 'deny' },
          { condition: 'Held ≥ 1 CXXR for < 7 days', outcome: 'Not eligible: minimum holding not met', type: 'deny' },
          { condition: 'Held ≥ 1 CXXR for 7–89 days', outcome: 'Pro-rata vesting: (holdingDays / 90) × balance RCXXR', type: 'allow' },
          { condition: 'Held ≥ 1 CXXR for full 90-day quarter', outcome: 'Full vesting: balance × 1.0 RCXXR', type: 'allow' },
        ],
      },
    ],
    services: ['tpe', 'wallet'],
    sourceFile: 'contracts/token/VestingContract.ts',
  },

  // ─────────────────────────────────────────────────────────────────
  // RESERVE DOMAIN
  // ─────────────────────────────────────────────────────────────────
  {
    id: 'PegContract',
    name: 'Peg Contract',
    domain: 'reserve',
    domainColor: DOMAIN_COLORS.reserve,
    description: 'Business rules for maintaining the 1:1 USD peg. All internal operations settle at exactly $1.00.',
    sections: ['§1.1.2 Peg Enforcement', '§1.1.5 Reserve Management'],
    constants: [
      { name: 'PEG_PRICE', value: 1.0, unit: 'USD', section: '§1.1.2' },
      { name: 'NORMAL_DEVIATION_PERCENT', value: 0.5, unit: '%', section: '§1.1.2' },
      { name: 'WARNING_DEVIATION_PERCENT', value: 1.0, unit: '%', section: '§1.1.2' },
      { name: 'CRITICAL_DEVIATION_PERCENT', value: 2.0, unit: '%', section: '§1.1.2' },
      { name: 'PRICE_CHECK_INTERVAL_MINUTES', value: 5, unit: 'min', section: '§1.1.2' },
    ],
    rules: [
      {
        id: 'peg-enforcement',
        summary: 'Internal settlement always uses exactly $1.00 per CXXR. External (CEX) price deviations trigger escalating alerts.',
        section: '§1.1.2',
        decisions: [
          { condition: 'CEX price within ±0.5% of $1.00', outcome: 'Normal: no action', type: 'allow' },
          { condition: 'CEX deviation 0.5%–1.0%', outcome: 'Warning alert', type: 'info' },
          { condition: 'CEX deviation 1.0%–2.0%', outcome: 'Critical alert to treasury + council', type: 'info' },
          { condition: 'CEX deviation > 2.0%', outcome: 'Emergency: potential split trigger, halt external exchange', type: 'deny' },
        ],
      },
    ],
    services: ['anchor', 'exchange', 'tpe'],
    sourceFile: 'contracts/reserve/PegContract.ts',
  },

  {
    id: 'ReserveContract',
    name: 'Reserve Contract',
    domain: 'reserve',
    domainColor: DOMAIN_COLORS.reserve,
    description: 'Business rules for maintaining the 2:1 reserve ratio — for every $1 of CXXR in circulation, there must be $2 in reserve assets.',
    sections: ['§1.1.2 Reserve Ratio', '§1.1.5 Reserve Management'],
    constants: [
      { name: 'REQUIRED_RESERVE_RATIO', value: 2.0, unit: ':1 (reserve:circulation)', section: '§1.1.5' },
      { name: 'WARNING_RESERVE_RATIO', value: 1.8, unit: ':1', section: '§1.1.5' },
      { name: 'CRITICAL_RESERVE_RATIO', value: 1.5, unit: ':1', section: '§1.1.5' },
      { name: 'RESERVE_CHECK_INTERVAL_HOURS', value: 1, unit: 'hours', section: '§1.1.5' },
    ],
    rules: [
      {
        id: 'reserve-enforcement',
        summary: 'The system checks the reserve ratio hourly. If reserve assets fall below 2:1, escalating actions are triggered.',
        section: '§1.1.2',
        decisions: [
          { condition: 'Ratio ≥ 2.0', outcome: 'Healthy: no action', type: 'allow' },
          { condition: 'Ratio 1.8–2.0', outcome: 'Warning: alert treasury, pause large mints', type: 'info' },
          { condition: 'Ratio 1.5–1.8', outcome: 'Critical: halt minting, alert council', type: 'deny' },
          { condition: 'Ratio < 1.5', outcome: 'Emergency: halt all operations except withdrawals', type: 'deny' },
        ],
      },
    ],
    services: [],
    sourceFile: 'contracts/reserve/ReserveContract.ts',
  },

  {
    id: 'ReserveTokenContract',
    name: 'Reserve Token (RCXXR) Contract',
    domain: 'reserve',
    domainColor: DOMAIN_COLORS.reserve,
    description: 'Business rules for the RCXXR non-transferable reserve token. RCXXR represents a claim on the reserve dividend pool.',
    sections: ['§1.1.2 Reserve Token'],
    constants: [
      { name: 'RCXXR_ACCOUNT_TYPE', value: 'rcxxr', section: '§1.1.2' },
      { name: 'TRANSFERS_ALLOWED', value: false, section: '§1.1.2' },
    ],
    rules: [
      {
        id: 'rcxxr-properties',
        summary: 'RCXXR is non-transferable, cannot be sent P2P, and can only be minted via token splits or quarterly vesting. It is burned when dividends are claimed.',
        section: '§1.1.2',
        decisions: [
          { condition: 'Attempt P2P transfer of RCXXR', outcome: 'Deny: RCXXR is non-transferable', type: 'deny' },
          { condition: 'Token split occurs', outcome: 'RCXXR minted 1:1 with CXXR balance', type: 'allow' },
          { condition: 'Quarterly vesting completes', outcome: 'RCXXR minted per vesting rules', type: 'allow' },
          { condition: 'Dividend claimed', outcome: 'RCXXR burned, USD credited', type: 'info' },
        ],
      },
    ],
    services: ['ledger', 'tpe'],
    sourceFile: 'contracts/reserve/ReserveTokenContract.ts',
  },

  // ─────────────────────────────────────────────────────────────────
  // EXCHANGE DOMAIN
  // ─────────────────────────────────────────────────────────────────
  {
    id: 'ExchangeContract',
    name: 'Exchange Contract',
    domain: 'exchange',
    domainColor: DOMAIN_COLORS.exchange,
    description: 'Business rules for CXXR buy/sell operations via the exchange service (CEX/DEX).',
    sections: ['§1.1.1 CEX Connectivity', '§1.2 Exchange Integration'],
    constants: [
      { name: 'MIN_EXCHANGE_AMOUNT', value: 10, unit: 'USD', section: '§1.1.1' },
      { name: 'MAX_EXCHANGE_AMOUNT', value: 10_000, unit: 'USD', section: '§1.1.1' },
      { name: 'MAX_DAILY_EXCHANGE_AMOUNT', value: 50_000, unit: 'USD', section: '§1.1.1' },
      { name: 'MAX_MONTHLY_EXCHANGE_AMOUNT', value: 250_000, unit: 'USD', section: '§1.1.1' },
      { name: 'EXCHANGE_REQUIRED_KYC_LEVEL', value: 'verified', section: '§1.1.1' },
    ],
    rules: [
      {
        id: 'exchange-validation',
        summary: 'Exchange (buy/sell) requires KYC verification and must be within per-transaction, daily, and monthly limits.',
        decisions: [
          { condition: 'KYC ≠ verified', outcome: 'Deny: KYC required', type: 'deny' },
          { condition: 'Amount < $10', outcome: 'Deny: below minimum', type: 'deny' },
          { condition: 'Amount > $10,000', outcome: 'Deny: above per-tx max', type: 'deny' },
          { condition: 'Daily total > $50,000', outcome: 'Deny: daily limit', type: 'deny' },
          { condition: 'Monthly total > $250,000', outcome: 'Deny: monthly limit', type: 'deny' },
          { condition: 'All checks pass', outcome: 'Allow: 0.5% fee applied', type: 'allow' },
        ],
      },
    ],
    services: ['exchange'],
    sourceFile: 'contracts/exchange/ExchangeContract.ts',
  },

  {
    id: 'SpeculationContract',
    name: 'Speculation Control Contract',
    domain: 'exchange',
    domainColor: DOMAIN_COLORS.exchange,
    description: 'Business rules to limit speculative trading that could destabilize the peg and reserve ratio.',
    sections: ['§1.1.2 Speculation Controls', '§1.1.5 Trading Limits'],
    constants: [
      { name: 'MAX_TRADES_PER_HOUR', value: 10, section: '§1.1.5' },
      { name: 'MAX_TRADES_PER_DAY', value: 50, section: '§1.1.5' },
      { name: 'MIN_TRADE_INTERVAL_SECONDS', value: 10, unit: 'sec', section: '§1.1.5' },
      { name: 'LARGE_ORDER_THRESHOLD', value: 5_000, unit: 'USD', section: '§1.1.5' },
      { name: 'LARGE_ORDER_DELAY_SECONDS', value: 60, unit: 'sec', section: '§1.1.5' },
      { name: 'RAPID_REVERSAL_WINDOW_MINUTES', value: 5, unit: 'min', section: '§1.1.5' },
    ],
    rules: [
      {
        id: 'velocity-limits',
        summary: 'Trading velocity is capped to prevent wash trading and manipulation. Large orders are delayed. Rapid buy-then-sell (or vice versa) is flagged.',
        section: '§1.1.2',
        decisions: [
          { condition: 'Trades this hour ≥ 10', outcome: 'Deny: hourly velocity limit', type: 'deny' },
          { condition: 'Trades today ≥ 50', outcome: 'Deny: daily velocity limit', type: 'deny' },
          { condition: 'Last trade < 10 seconds ago', outcome: 'Deny: minimum interval', type: 'deny' },
          { condition: 'Order ≥ $5,000', outcome: 'Delay: 60-second hold before execution', type: 'info' },
          { condition: 'Buy then sell (or vice versa) within 5 min', outcome: 'Flag: rapid reversal warning', type: 'info' },
          { condition: 'All checks pass', outcome: 'Allow trade', type: 'allow' },
        ],
      },
    ],
    services: ['exchange'],
    sourceFile: 'contracts/exchange/SpeculationContract.ts',
  },

  // ─────────────────────────────────────────────────────────────────
  // COMPLIANCE DOMAIN
  // ─────────────────────────────────────────────────────────────────
  {
    id: 'KycContract',
    name: 'KYC Contract',
    domain: 'compliance',
    domainColor: DOMAIN_COLORS.compliance,
    description: 'Business rules for Know Your Customer requirements under Regulation A Tier 2.',
    sections: ['§1.1.2 Token Ownership / KYC', '§3.1 Regulatory Compliance'],
    constants: [
      { name: 'KYC_EXPIRY_DAYS', value: 365, unit: 'days', section: '§3.1' },
      { name: 'REVERIFICATION_THRESHOLD', value: 100_000, unit: 'USD', section: '§3.1' },
      { name: 'COUNTRY_LIST_UPDATE_DAYS', value: 30, unit: 'days', section: '§3.1' },
    ],
    rules: [
      {
        id: 'kyc-tiers',
        summary: 'Users progress through KYC tiers that gate which operations they can perform and the associated limits.',
        section: '§1.1.2',
        decisions: [
          { condition: 'KYC = none', outcome: 'Can browse only; no transactions', type: 'deny' },
          { condition: 'KYC = pending', outcome: 'Can browse only; awaiting review', type: 'deny' },
          { condition: 'KYC = verified', outcome: 'Full access: mint, burn, transfer, exchange', type: 'allow' },
          { condition: 'KYC = rejected', outcome: 'Blocked: must reapply', type: 'deny' },
          { condition: 'KYC expired (> 365 days)', outcome: 'Must re-verify before transacting', type: 'deny' },
          { condition: 'Cumulative volume > $100,000', outcome: 'Enhanced due diligence / re-verification required', type: 'info' },
        ],
      },
    ],
    services: ['kyc', 'exchange'],
    sourceFile: 'contracts/compliance/KycContract.ts',
  },

  {
    id: 'AmlContract',
    name: 'AML Contract',
    domain: 'compliance',
    domainColor: DOMAIN_COLORS.compliance,
    description: 'Business rules for Anti-Money Laundering compliance including CTR and SAR thresholds.',
    sections: ['§1.1.2 KYC/AML Reporting', '§3.1 Regulatory Compliance'],
    constants: [
      { name: 'CTR_THRESHOLD', value: 10_000, unit: 'USD', section: '§3.1' },
      { name: 'SAR_THRESHOLD', value: 10_000, unit: 'USD', section: '§3.1' },
      { name: 'STRUCTURING_THRESHOLD', value: 0.8, unit: '× SAR threshold (= $8,000)', section: '§3.1' },
      { name: 'STRUCTURING_WINDOW_DAYS', value: 7, unit: 'days', section: '§3.1' },
    ],
    rules: [
      {
        id: 'aml-reporting',
        summary: 'Transactions at or above $10,000 trigger a Currency Transaction Report (CTR). Suspicious patterns near thresholds trigger a SAR review.',
        section: '§1.1.2',
        decisions: [
          { condition: 'Single transaction ≥ $10,000', outcome: 'File CTR with FinCEN', type: 'info' },
          { condition: 'Activity triggers suspicion (any amount)', outcome: 'File SAR with FinCEN', type: 'info' },
          { condition: 'Multiple transactions totaling ≥ $8,000 within 7 days', outcome: 'Flag: potential structuring; SAR review', type: 'info' },
          { condition: 'Transaction from sanctioned country', outcome: 'Block transaction; file SAR', type: 'deny' },
        ],
      },
    ],
    services: [],
    sourceFile: 'contracts/compliance/AmlContract.ts',
  },

  // ─────────────────────────────────────────────────────────────────
  // GOVERNANCE DOMAIN
  // ─────────────────────────────────────────────────────────────────
  {
    id: 'GovernanceContract',
    name: 'Governance Contract',
    domain: 'governance',
    domainColor: DOMAIN_COLORS.governance,
    description: 'Business rules for the Council of Eight governance mechanism with voting and veto powers.',
    sections: ['§1.1.2 Governance Mechanisms'],
    constants: [
      { name: 'COUNCIL_SIZE', value: 8, unit: 'members', section: '§1.1.2' },
      { name: 'QUORUM', value: 5, unit: 'votes required', section: '§1.1.2' },
      { name: 'VETO_THRESHOLD', value: 3, unit: 'vetoes to block', section: '§1.1.2' },
      { name: 'VOTING_PERIOD_DAYS', value: 7, unit: 'days', section: '§1.1.2' },
      { name: 'EXECUTION_DELAY_HOURS', value: 48, unit: 'hours', section: '§1.1.2' },
      { name: 'MIN_TITLE_LENGTH', value: 10, unit: 'chars', section: '§1.1.2' },
      { name: 'MIN_DESCRIPTION_LENGTH', value: 50, unit: 'chars', section: '§1.1.2' },
    ],
    rules: [
      {
        id: 'governance-voting',
        summary: 'Proposals require a quorum of 5 out of 8 council members to pass. 3 or more vetoes block a proposal regardless of approval count. Approved proposals have a 48-hour delay before execution.',
        section: '§1.1.2',
        decisions: [
          { condition: 'Votes (approve + reject) < 5', outcome: 'No quorum: proposal remains open', type: 'info' },
          { condition: 'Vetoes ≥ 3', outcome: 'Vetoed: proposal blocked regardless of votes', type: 'deny' },
          { condition: 'Approvals ≥ quorum AND vetoes < 3', outcome: 'Approved: 48-hour execution delay begins', type: 'allow' },
          { condition: 'Rejections ≥ quorum AND vetoes < 3', outcome: 'Rejected by vote', type: 'deny' },
          { condition: 'Voting period (7 days) expires without quorum', outcome: 'Expired: proposal archived', type: 'deny' },
        ],
      },
    ],
    services: ['governance'],
    sourceFile: 'contracts/governance/GovernanceContract.ts',
  },
];

// =============================================================================
// HELPERS
// =============================================================================

/** Get all unique domains */
export function getDomains(): string[] {
  const seen = new Set<string>();
  for (const c of CONTRACT_RULES) {
    seen.add(c.domain);
  }
  return Array.from(seen);
}

/** Get contracts grouped by domain */
export function getContractsByDomain(): Record<string, ContractDefinition[]> {
  const groups: Record<string, ContractDefinition[]> = {};
  for (const c of CONTRACT_RULES) {
    if (!groups[c.domain]) groups[c.domain] = [];
    groups[c.domain].push(c);
  }
  return groups;
}

/** Get key parameters for the summary widget */
export interface KeyParameter {
  label: string;
  value: string;
  contract: string;
  section?: string;
}

export function getKeyParameters(): KeyParameter[] {
  return [
    { label: 'Mint Fee', value: '5%', contract: 'FeeContract', section: '§1.1.2' },
    { label: 'Transfer/Exchange/Withdrawal Fee', value: '0.5%', contract: 'FeeContract', section: '§1.1.2' },
    { label: 'Fee Split', value: '47.5 / 47.5 / 5', contract: 'FeeContract', section: '§2.3.1' },
    { label: 'USD Peg', value: '$1.00', contract: 'PegContract', section: '§1.1.2' },
    { label: 'Reserve Ratio', value: '2:1', contract: 'ReserveContract', section: '§1.1.2' },
    { label: 'Council Size', value: '8', contract: 'GovernanceContract', section: '§1.1.2' },
    { label: 'Quorum', value: '5 of 8', contract: 'GovernanceContract', section: '§1.1.2' },
    { label: 'Veto Threshold', value: '3', contract: 'GovernanceContract', section: '§1.1.2' },
    { label: 'CTR Threshold', value: '$10,000', contract: 'AmlContract', section: '§1.1.2' },
    { label: 'Vesting Period', value: '90 days', contract: 'VestingContract' },
  ];
}

/** Domain display labels */
export const DOMAIN_LABELS: Record<string, string> = {
  fees: 'Fees & Dividends',
  token: 'Token Operations',
  reserve: 'Reserve Management',
  exchange: 'Exchange & Trading',
  compliance: 'Compliance (KYC/AML)',
  governance: 'Governance',
};

/** Domain order for display */
export const DOMAIN_ORDER = ['token', 'fees', 'reserve', 'exchange', 'compliance', 'governance'];
